import { Key } from 'antd/es/table/interface';
import { ErrorMappingValueType } from 'typings';
import { ADMIN_ROUTES } from 'constants/routes';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MappedKeys } from 'components/common/transferComponent';
import { Form, FormProps, notification } from 'components';
import { usePermissionListQuery, useRoleByIdQuery, useRoleCreateMutation, useRoleUpdateMutation } from 'api';

interface IRoleForm {
  id?: string;
  name: string;
  permissions: MappedKeys;
  description: string;
}

export const useAddEdit = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [targetKeys, setTargetKeys] = useState<Key[]>([]);
  const [mappedTargetKeys, setMappedTargetKeys] = useState<MappedKeys>({});
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const [notificationApi, contextHolder] = notification.useNotification();

  const { data: roleDetails = null } = useRoleByIdQuery({ id }, { skip: !id });
  const {
    data: permissionData = {},
    isLoading: permissionIsLoading,
    isFetching,
  } = usePermissionListQuery({
    page: currentPage,
    size: 10,
    search,
  });
  const [createRole, { isLoading: createIsLoading }] = useRoleCreateMutation();
  const [updateRole, { isLoading: updateIsLoading }] = useRoleUpdateMutation();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({ message });
    },
    [notificationApi]
  );
  const onChange = useCallback((targetKeys: Key[], mappedKeys: MappedKeys) => {
    setTargetKeys(targetKeys);
    setMappedTargetKeys(mappedKeys);
  }, []);
  const onSearch = useCallback((value: string) => {
    setCurrentPage(1);
    setSearch(value);
  }, []);
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);

  const onValuesChange = useCallback(() => setDisableSaveBtn(false), []);

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    navigate(id ? `${ADMIN_ROUTES.role.view}/${id}/${ADMIN_ROUTES.role.permissions}` : ADMIN_ROUTES.role.list);
  }, [form, navigate, id]);

  const cancelWithNotification = useCallback(() => {
    form.resetFields();
    navigate(id ? `${ADMIN_ROUTES.role.view}/${id}/${ADMIN_ROUTES.role.permissions}` : ADMIN_ROUTES.role.list, {
      state: {
        notification: {
          type: 'success',
          message: `Role was successfully ${roleDetails ? 'updated' : 'created'}.`,
        },
      },
    });
  }, [form, navigate, id, roleDetails]);

  const onFinish: FormProps<IRoleForm>['onFinish'] = useCallback(
    async (values: IRoleForm) => {
      const payload = values;
      payload.name = payload.name.trim(); // Trim before submitting
      if (roleDetails) {
        payload.id = roleDetails.id;
      }
      payload.permissions = mappedTargetKeys;
      try {
        const { data, error }: any = await (roleDetails ? updateRole : createRole)(values);
        if (data?.success) {
          cancelWithNotification();
        } else {
          handleErrors(error);
        }
      } catch (err) {
        handleErrors(err);
      }
    },
    [createRole, updateRole, cancelWithNotification, handleErrors, roleDetails, mappedTargetKeys]
  );

  useEffect(() => {
    if (id && roleDetails) {
      const permissions: Key[] = Object.values(roleDetails.permissions).flat() as Key[];
      form.setFieldsValue({ ...roleDetails, permissions });
      setTargetKeys(permissions);
      setMappedTargetKeys(roleDetails.permissions);
    }
  }, [id, roleDetails, form]);

  return {
    form,
    onCancel,
    onFinish,
    isLoading: updateIsLoading || createIsLoading,
    disableSaveBtn,
    roleDetails,
    permissionData,
    isPermissionLoading: permissionIsLoading || isFetching,
    targetKeys,
    onChange,
    onSearch,
    handlePageChange,
    onValuesChange,
    contextHolder,
  };
};
